import { useState, useRef, useEffect, useId, type KeyboardEvent } from 'react'
import { ChevronLeft, ChevronRight, Calendar as CalendarIcon } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { MENU_SURFACE } from '@/components/ui/menu-surface'
import { Select } from '@/components/ui/select'

const MONTH_NAMES = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
]
const WEEKDAY_LABELS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa']

function parseISODate(value: string): { year: number; month: number; day: number } | null {
  if (!value) return null
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value)
  if (!match) return null
  return { year: Number(match[1]), month: Number(match[2]) - 1, day: Number(match[3]) }
}

function formatISODate(year: number, month: number, day: number): string {
  return `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`
}

function daysInMonth(year: number, month: number): number {
  return new Date(year, month + 1, 0).getDate()
}

function formatDisplay(value: string): string {
  const parsed = parseISODate(value)
  if (!parsed) return ''
  return new Date(parsed.year, parsed.month, parsed.day).toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  })
}

const MONTH_ABBREVIATIONS = MONTH_NAMES.map((m) => m.slice(0, 3).toLowerCase())

/** Parses free-typed text into an ISO date, accepting the formats a user is likely to type:
 *  ISO (2026-09-18), day/month/year with '/' or '-' (18/09/2026 — the Indian convention), and
 *  this picker's own display format (18 Sep 2026), so re-typing what's shown round-trips.
 *  Returns null — never throws — for anything unparseable, out of [minYear, maxYear], or a
 *  syntactically fine but impossible date like 31/02/2026. */
function parseTypedDate(raw: string, minYear: number, maxYear: number): string | null {
  const text = raw.trim()
  if (!text) return null

  let year: number, month: number, day: number

  let match = /^(\d{4})-(\d{1,2})-(\d{1,2})$/.exec(text)
  if (match) {
    year = Number(match[1])
    month = Number(match[2]) - 1
    day = Number(match[3])
  } else if ((match = /^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/.exec(text))) {
    day = Number(match[1])
    month = Number(match[2]) - 1
    year = Number(match[3])
  } else if ((match = /^(\d{1,2})\s+([A-Za-z]+)\s+(\d{4})$/.exec(text))) {
    day = Number(match[1])
    const monthIndex = MONTH_ABBREVIATIONS.indexOf(match[2].slice(0, 3).toLowerCase())
    if (monthIndex === -1) return null
    month = monthIndex
    year = Number(match[3])
  } else {
    return null
  }

  if (year < minYear || year > maxYear) return null
  if (month < 0 || month > 11) return null
  if (day < 1 || day > daysInMonth(year, month)) return null

  return formatISODate(year, month, day)
}

export function DatePicker({
  value,
  onChange,
  minYear = 1970,
  maxYear = 2100,
  id,
  defaultToday = false,
}: {
  value: string
  onChange: (value: string) => void
  minYear?: number
  maxYear?: number
  id?: string
  /** Preselect today when the popover is opened with no value chosen. Fires once —
   *  if the user then clears the field it stays empty for the rest of the session. */
  defaultToday?: boolean
}) {
  const generatedId = useId()
  const errorId = `${id ?? generatedId}-error`
  const [open, setOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const clearedByUser = useRef(false)
  const parsed = parseISODate(value)
  const today = new Date()
  const [viewYear, setViewYear] = useState(parsed?.year ?? today.getFullYear())
  const [viewMonth, setViewMonth] = useState(parsed?.month ?? today.getMonth())

  // The text field's own state — kept separate from `value` so an invalid in-progress edit
  // (and its error message) survives re-renders instead of being clobbered back to the last
  // valid display text. Resynced from `value` below whenever it actually changes (our own
  // successful commit, a calendar pick, or an external reset) — the "adjust state during
  // render" pattern, not an effect, so there's no extra render before the DOM reflects it.
  const [text, setText] = useState(formatDisplay(value))
  const [error, setError] = useState<string | null>(null)
  const [prevValue, setPrevValue] = useState(value)
  if (value !== prevValue) {
    setPrevValue(value)
    setText(formatDisplay(value))
    setError(null)
  }

  useEffect(() => {
    function handleOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    if (open) document.addEventListener('mousedown', handleOutside)
    return () => document.removeEventListener('mousedown', handleOutside)
  }, [open])

  const years: number[] = []
  for (let y = minYear; y <= maxYear; y++) years.push(y)

  const firstWeekday = new Date(viewYear, viewMonth, 1).getDay()
  const totalDays = daysInMonth(viewYear, viewMonth)
  const cells: (number | null)[] = [
    ...Array.from({ length: firstWeekday }, () => null),
    ...Array.from({ length: totalDays }, (_, i) => i + 1),
  ]

  // Resets the visible month/year to match the current value (or today, if empty)
  // exactly when the calendar opens — not reactively via an effect — so a form reset
  // while the popover is closed can't clobber in-progress navigation, and a field
  // that's never been opened doesn't need its view state kept in sync at all.
  function openCalendar() {
    let seedYear = parsed?.year ?? today.getFullYear()
    let seedMonth = parsed?.month ?? today.getMonth()
    if (defaultToday && !value && !clearedByUser.current) {
      onChange(formatISODate(today.getFullYear(), today.getMonth(), today.getDate()))
      seedYear = today.getFullYear()
      seedMonth = today.getMonth()
    }
    setViewYear(seedYear)
    setViewMonth(seedMonth)
    setOpen(true)
  }

  function selectDay(day: number) {
    onChange(formatISODate(viewYear, viewMonth, day))
    setOpen(false)
  }

  function clear() {
    clearedByUser.current = true
    onChange('')
    setOpen(false)
  }

  /** Commits whatever's currently typed — called on blur and on Enter. An empty field clears
   *  the value; unparseable or out-of-range text is left in place with an inline error instead
   *  of being silently discarded, so the user can see and fix what's wrong. */
  function commitTypedText() {
    if (!text.trim()) {
      setError(null)
      if (value) {
        clearedByUser.current = true
        onChange('')
      }
      return
    }
    const iso = parseTypedDate(text, minYear, maxYear)
    if (!iso) {
      setError(
        `Enter a valid date, e.g. ${formatDisplay(formatISODate(today.getFullYear(), today.getMonth(), today.getDate()))}`
      )
      return
    }
    setError(null)
    clearedByUser.current = false
    setText(formatDisplay(iso))
    onChange(iso)
  }

  function handleTextKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter') {
      e.preventDefault()
      commitTypedText()
      e.currentTarget.blur()
    } else if (e.key === 'Escape') {
      e.preventDefault()
      setText(formatDisplay(value))
      setError(null)
      setOpen(false)
    }
  }

  function goToMonth(delta: number) {
    let m = viewMonth + delta
    let y = viewYear
    if (m < 0) {
      m = 11
      y -= 1
    } else if (m > 11) {
      m = 0
      y += 1
    }
    setViewMonth(m)
    setViewYear(y)
  }

  function isSelected(day: number) {
    return !!parsed && parsed.year === viewYear && parsed.month === viewMonth && parsed.day === day
  }
  function isToday(day: number) {
    return (
      today.getFullYear() === viewYear && today.getMonth() === viewMonth && today.getDate() === day
    )
  }

  return (
    <div className="relative" ref={containerRef}>
      <div className="relative">
        <Input
          id={id}
          type="text"
          value={text}
          placeholder="Select date"
          onChange={(e) => setText(e.target.value)}
          onKeyDown={handleTextKeyDown}
          onBlur={commitTypedText}
          aria-invalid={!!error}
          aria-describedby={error ? errorId : undefined}
          className="pr-9"
        />
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          tooltip="Open calendar"
          aria-expanded={open}
          aria-haspopup="dialog"
          onClick={() => (open ? setOpen(false) : openCalendar())}
          className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 text-[var(--fl-text-3)]"
        >
          <CalendarIcon size={15} />
        </Button>
      </div>
      {error && (
        <p id={errorId} className="mt-1 text-xs text-[var(--fl-error)]">
          {error}
        </p>
      )}
      {open && (
        <div
          role="dialog"
          aria-label="Choose date"
          className={`${MENU_SURFACE} absolute z-20 mt-1 w-80 p-3`}
        >
          <div className="flex items-center gap-1 mb-3">
            <Button
              type="button"
              variant="ghost"
              size="icon-sm"
              tooltip="Previous month"
              onClick={() => goToMonth(-1)}
              className="text-[var(--fl-text-2)]"
            >
              <ChevronLeft size={16} />
            </Button>
            <div className="flex-1">
              <Select
                aria-label="Month"
                size="sm"
                value={String(viewMonth)}
                onChange={(v) => setViewMonth(Number(v))}
                className="w-full"
              >
                {MONTH_NAMES.map((m, i) => (
                  <option key={m} value={i}>
                    {m}
                  </option>
                ))}
              </Select>
            </div>
            <div className="w-24 shrink-0">
              <Select
                aria-label="Year"
                size="sm"
                value={String(viewYear)}
                onChange={(v) => setViewYear(Number(v))}
                className="w-full"
              >
                {years.map((y) => (
                  <option key={y} value={y}>
                    {y}
                  </option>
                ))}
              </Select>
            </div>
            <Button
              type="button"
              variant="ghost"
              size="icon-sm"
              tooltip="Next month"
              onClick={() => goToMonth(1)}
              className="text-[var(--fl-text-2)]"
            >
              <ChevronRight size={16} />
            </Button>
          </div>

          <div className="grid grid-cols-7 justify-items-center gap-1 mb-1">
            {WEEKDAY_LABELS.map((w) => (
              <div
                key={w}
                className="text-center text-[10px] font-semibold text-[var(--fl-text-2)]"
              >
                {w}
              </div>
            ))}
          </div>
          <div className="grid grid-cols-7 justify-items-center gap-1">
            {cells.map((day, i) =>
              day === null ? (
                <div key={`blank-${i}`} />
              ) : (
                <button
                  key={day}
                  type="button"
                  onClick={() => selectDay(day)}
                  className={[
                    'h-8 w-8 rounded-lg text-sm flex items-center justify-center transition-colors',
                    isSelected(day)
                      ? 'bg-[var(--fl-primary)] text-[var(--fl-primary-fg)] font-semibold'
                      : isToday(day)
                        ? 'bg-[var(--fl-surface-hover)] text-[var(--fl-heading)] font-semibold'
                        : 'text-[var(--fl-text-1)] hover:bg-[var(--fl-surface-hover)]',
                  ].join(' ')}
                >
                  {day}
                </button>
              )
            )}
          </div>
          <Button
            type="button"
            variant="ghost"
            onClick={clear}
            className="mt-2 w-full h-auto px-0 py-1 text-xs font-medium text-[var(--fl-text-2)]"
          >
            Clear
          </Button>
        </div>
      )}
    </div>
  )
}
